// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Patient } from '@medplum/fhirtypes';
import { LOINC_SYSTEM, PATIENT_EXTENSION_URLS } from '../constants/patient-profile';

export function getBirthSex(patient: Patient): string {
  const ext = patient.extension?.find((e) => e.url === PATIENT_EXTENSION_URLS.birthSex);
  return ext?.valueCoding?.code ?? ext?.valueCode ?? '';
}

export function setBirthSex(patient: Patient, code: string, system: string, display: string): Patient {
  const extensions = (patient.extension ?? []).filter((e) => e.url !== PATIENT_EXTENSION_URLS.birthSex);
  if (code) {
    extensions.push({
      url: PATIENT_EXTENSION_URLS.birthSex,
      valueCoding: { system, code, display },
    });
  }
  return { ...patient, extension: extensions };
}

export function getGenderIdentity(patient: Patient): string {
  const ext = patient.extension?.find((e) => e.url === PATIENT_EXTENSION_URLS.genderIdentity);
  return ext?.valueCoding?.code ?? patient.gender ?? '';
}

export function setGenderIdentity(
  patient: Patient,
  fhirGender: string,
  code: string,
  display: string,
  system: string
): Patient {
  const extensions = (patient.extension ?? []).filter((e) => e.url !== PATIENT_EXTENSION_URLS.genderIdentity);
  if (code) {
    extensions.push({
      url: PATIENT_EXTENSION_URLS.genderIdentity,
      valueCoding: { system, code, display },
    });
  }
  return {
    ...patient,
    gender: (fhirGender as Patient['gender']) || undefined,
    extension: extensions,
  };
}

export function getPronouns(patient: Patient): string[] {
  return (patient.extension ?? [])
    .filter((e) => e.url === PATIENT_EXTENSION_URLS.pronouns)
    .map((e) => e.valueCodeableConcept?.coding?.[0]?.code ?? '')
    .filter(Boolean);
}

export function setPronouns(patient: Patient, selections: { code: string; display: string }[]): Patient {
  const extensions = (patient.extension ?? []).filter((e) => e.url !== PATIENT_EXTENSION_URLS.pronouns);
  for (const { code, display } of selections) {
    extensions.push({
      url: PATIENT_EXTENSION_URLS.pronouns,
      valueCodeableConcept: {
        coding: [{ system: LOINC_SYSTEM, code, display }],
      },
    });
  }
  return { ...patient, extension: extensions };
}
